import { ReactNode } from 'react';
import { Term } from '../Term';
import { EVASION, HIT_POINTS, STRESS, HOPE, LOADOUT, VAULT, RECALL_COST } from './templates';

export interface StepLink {
  label: string;
  path: string;
}

export interface Step {
  step: number;
  title: string;
  description: ReactNode;
  details: ReactNode;
  links?: StepLink[];
}

const steps: Step[] = [
  {
    step: 1,
    title: 'Choose a Class and Subclass',
    description: (
      <p>
        <strong>Classes</strong> are role-based archetypes that determine which class
        features and <Term>domain cards</Term> a
        PC gains access to throughout the campaign. There are nine classes in this SRD:
        Bard, Druid, Guardian, Ranger, Rogue, Seraph, Sorcerer, Warrior, and Wizard.
        Each class has two <strong>subclasses</strong> that refine your character's
        expression and grant access to unique subclass features.
      </p>
    ),
    details: (
      <ul>
        <li>Select a class and take its corresponding <strong>character sheet</strong> and <strong>character guide</strong> printouts</li>
        <li>Every class begins with one or more unique <strong>class features</strong> described at the bottom of the character sheet</li>
        <li>Choose one of the two subclasses and take its <strong>Foundation card</strong></li>
        <li>Record your Starting {EVASION}, {HIT_POINTS}, and any class-specific starting items</li>
      </ul>
    ),
    links: [
      { label: 'Browse Classes', path: '/classes' },
      { label: 'Browse Domains', path: '/domains' },
    ],
  },
  {
    step: 2,
    title: 'Choose Your Heritage',
    description: (
      <p>
        Your character's <strong>heritage</strong> combines two elements:{' '}
        <Term>ancestry</Term> and{' '}
        <Term>community</Term>. Your
        ancestry reflects your lineage and grants two unique <strong>ancestry features</strong>.
        Your community represents your cultural upbringing and grants a{' '}
        <strong>community feature</strong>. To create a Mixed Ancestry, take the top
        (first-listed) feature from one ancestry and the bottom (second-listed) feature
        from another.
      </p>
    ),
    details: (
      <ul>
        <li>Choose one of 15 <Term>ancestries</Term> - take the card and write its name in the Heritage field</li>
        <li>Choose one of 9 <Term>communities</Term> - take the card and write its name in the Heritage field</li>
        <li>Record both ancestry features on your character sheet</li>
        <li>Record your community feature on your character sheet</li>
      </ul>
    ),
    links: [
      { label: 'Browse Ancestries', path: '/ancestries' },
      { label: 'Browse Communities', path: '/communities' },
    ],
  },
  {
    step: 3,
    title: 'Assign Character Traits',
    description: (
      <p>
        Your character has six <strong>traits</strong> that represent their physical,
        mental, and social aptitude. When you "roll with a trait," add that trait's
        modifier to the roll's total. Assign the modifiers <strong>+2, +1, +1, +0, +0, −1</strong> to
        your six traits in any order you wish - your class suggests which to prioritize.
      </p>
    ),
    details: (
      <ul>
        <li><strong>Agility</strong> - Sprint, Leap, Maneuver. Fast on your feet, nimble on difficult terrain.</li>
        <li><strong>Strength</strong> - Lift, Smash, Grapple. Break through doors, lift heavy objects, hold ground.</li>
        <li><strong>Finesse</strong> - Control, Hide, Tinker. Precise, stealthy, fine motor work.</li>
        <li><strong>Instinct</strong> - Perceive, Sense, Navigate. Keen awareness and natural intuition.</li>
        <li><strong>Presence</strong> - Charm, Perform, Deceive. Personality, persuasion, and crowd command.</li>
        <li><strong>Knowledge</strong> - Recall, Analyze, Comprehend. Deduction, research, and inference.</li>
      </ul>
    ),
    links: [
      { label: 'Mechanics Reference', path: '/mechanics' },
    ],
  },
  {
    step: 4,
    title: 'Record Additional Information',
    description: (
      <p>
        Fill in the derived statistics that define how your character interacts with
        the world mechanically - their {HIT_POINTS},{' '}
        {STRESS} slots, {EVASION} score, and starting{' '}
        {HOPE}. These come from your class and may be modified by your
        ancestry or community features.
      </p>
    ),
    details: (
      <ul>
        <li>{STRESS} reflects ability to withstand mental and physical exertion. Every PC starts with <strong>6 Stress slots</strong>.</li>
        <li>{HOPE} fuels special moves and features. All PCs start with <strong>2 Hope</strong> - mark these in the Hope field.</li>
        <li>{EVASION} represents your character's ability to avoid damage - determined by class, recorded beneath the Evasion field.</li>
        <li>{HIT_POINTS} (HP) are your physical health measure - starting HP is determined by class and recorded on your sheet.</li>
      </ul>
    ),
    links: [
      { label: 'Mechanics Reference', path: '/mechanics' },
    ],
  },
  {
    step: 5,
    title: 'Choose Starting Equipment',
    description: (
      <p>
        Choose your starting{' '}
        <Term>weapons</Term> and{' '}
        <Term>armor</Term> from the Tier 1
        tables. Select either a <strong>two-handed primary weapon</strong> or a{' '}
        <strong>one-handed primary weapon and a one-handed secondary weapon</strong>,
        then equip your selection in the Active Weapon field. Also choose one set of
        armor from the Tier 1 Armor Table and record it in the Active Armor field.
      </p>
    ),
    details: (
      <ul>
        <li>At Level 1, your <strong>Proficiency</strong> is 1 - record this on your character sheet, then calculate your <strong>damage roll</strong> by combining Proficiency with your weapon's damage dice</li>
        <li>Add your equipped armor's <strong>Base Thresholds</strong> and record both numbers in the corresponding fields. Your <strong>Armor Score</strong> equals the armor's Base Score plus permanent bonuses</li>
        <li>Add to your <strong>Inventory</strong>: a torch, 50 feet of rope, basic supplies, a handful of gold; plus EITHER a Minor Health Potion OR a Minor Stamina Potion</li>
        <li>Take any class-specific starting items listed on your character guide</li>
      </ul>
    ),
    links: [
      { label: 'Weapons & Armor', path: '/equipment' },
      { label: 'Items & Consumables', path: '/items' },
    ],
  },
  {
    step: 6,
    title: 'Create Your Background',
    description: (
      <p>
        Develop your character's <strong>background</strong> by answering the
        background questions in your character guide, modifying or replacing them
        if they don't fit the character you want to play. Your background has no
        explicit mechanical effect, but it greatly affects how you play and the
        prep the GM will do.
      </p>
    ),
    details: (
      <ul>
        <li>Answer the background questions for your class in your character guide</li>
        <li>You can adjust choices made in earlier steps to better reflect your emerging background</li>
        <li>You can leave your character's past ambiguous and discover their backstory through play</li>
        <li>Consider what drives your character to adventure alongside these particular people</li>
      </ul>
    ),
  },
  {
    step: 7,
    title: 'Create Your Experiences',
    description: (
      <p>
        An <strong>Experience</strong> is a word or phrase that encapsulates a
        specific set of skills, personality traits, or aptitudes your character has
        acquired over their lifetime. When your PC makes a move, they can spend a
        Hope to add a relevant Experience's modifier to an action or reaction roll.
        Your PC gets <strong>two Experiences</strong> at character creation, each
        with a <strong>+2 modifier</strong>.
      </p>
    ),
    details: (
      <ul>
        <li>Experiences can't be too broadly applicable and can't grant character-specific mechanical benefits - <em>"Lucky"</em> and <em>"Highly Skilled"</em> are too broad</li>
        <li><strong>Backgrounds:</strong> Assassin, Blacksmith, Bodyguard, Bounty Hunter, Circus Performer, Field Medic, Merchant, Runaway Scholar, Soldier, Thief...</li>
        <li><strong>Characteristics:</strong> Affable, Battle-Hardened, Bookworm, Charming, Cowardly, Leader, Observant, Prankster, Stubborn to a Fault...</li>
        <li><strong>Skills:</strong> Animal Whisperer, Deadly Aim, Fast Learner, Incredible Strength, Liar, Negotiator, Photographic Memory, Quick Hands...</li>
      </ul>
    ),
  },
  {
    step: 8,
    title: 'Choose Domain Cards',
    description: (
      <p>
        Your class has access to two of the nine{' '}
        <Term>Domains</Term> included in the
        core set. Choose two cards from your class's domains - the domains are listed
        in the upper left of your character sheet. You can take one card from each
        domain or two cards from a single domain, whichever you prefer.
      </p>
    ),
    details: (
      <ul>
        <li>Only Level 1 cards are available at character creation</li>
        <li>Choose a {LOADOUT} - the cards you have active and ready to use</li>
        <li>Extra cards go in your {VAULT} and can be swapped in during a rest</li>
        <li>Each card has a {RECALL_COST} - spend that many Hope to move a card from your Vault to your Loadout</li>
      </ul>
    ),
    links: [
      { label: 'Browse Domain Cards', path: '/domains' },
    ],
  },
  {
    step: 9,
    title: 'Create Your Connections',
    description: (
      <p>
        <strong>Connections</strong> are the relationships between the PCs. Go around
        the table and have each player describe their character - at minimum, their
        name, pronouns, character description, experiences, and background question
        answers. Then discuss potential connections and suggest at least one connection
        between your character and each other player's PC.
      </p>
    ),
    details: (
      <ul>
        <li>A player can reject a suggested connection for any reason</li>
        <li>It's okay if there isn't an established connection between every pair of PCs - you can always discover and develop them through play</li>
        <li>Use the <strong>Connections</strong> section of your character guide as inspiration for discussion questions</li>
        <li>Accept any connections you want to explore, reject any you don't</li>
      </ul>
    ),
  },
];

export default steps;
